import React, { useState } from "react";
import { Globe, Type, Tag, Check, ChevronsUpDown, X } from "lucide-react";
import {
  CardTitle,
  CardDescription,
  CardContent,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogHeader,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectContent,
} from "../../components/ui/select";
import PreferenceItem from "./PreferenceItem";
import SectionHeader from "./SectionHeader";
import KeywordPill from "./KeywordPill";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover"
import { cn } from "../../lib/utils"
import { Button } from "../../components/ui/button"
import { useTranslation } from "../../i18n/hooks";

const toneOfVoiceOptionsList = [
  {
    value: "Professional & Trustworthy",
    description: "Ideal for premium/luxury brands, corporate stores",
    example: "Crafted with precision, this Italian leather wallet is designed for those who appreciate timeless quality. With RFID protection and a sleek silhouette, it offers both security and sophistication.",
    prompt: "Create content that emphasizes quality, reliability, and expertise. Use precise language with a formal but accessible tone. Focus on specifications, craftsmanship, and practical benefits. Maintain a confident voice that builds credibility and conveys authority without being pretentious. Ideal for premium brands that want to project competence and sophistication."
  },
  {
    value: "Playful & Quirky",
    description: "Great for fun, youthful brands",
    example: "Lost your wallet again? Not this time! This sleek Italian leather wallet keeps your cash, cards, and sanity in check—plus, it's got RFID protection, so digital pickpockets don't stand a chance.",
    prompt: "Write with an energetic, conversational tone that incorporates humor and personality. Use casual language, rhetorical questions, and playful phrases that create a sense of fun. Balance informality with product information, ensuring features are communicated amid the wit. Speak directly to the customer as if having a conversation with a friend. Ideal for brands wanting to appear approachable and entertaining."
  },
  {
    value: "Minimalist & Direct",
    description: "For modern, no-frills brands that value simplicity",
    example: "Italian leather. RFID protection. Slim, stylish, and built to last. A wallet made for the essentials—nothing more, nothing less.",
    prompt: "Communicate with brevity and clarity, focusing only on essential information. Use short sentences, fragments when appropriate, and eliminate unnecessary adjectives. Prioritize functional benefits in a straightforward manner. Avoid flowery language or excessive description. Ideal for brands that value efficiency and straightforwardness in their communication."
  },
  {
    value: "Luxury & Elegant",
    description: "For high-end brands with a sophisticated audience",
    example: "Handcrafted from the finest Italian leather, this wallet is more than an accessory—it's a statement. Designed for the modern connoisseur, it blends security with style, featuring RFID-blocking technology in an exquisitely slim profile.",
    prompt: "Craft sophisticated content that evokes exclusivity and refinement. Use rich, evocative language that appeals to sensory experiences. Emphasize craftsmanship, heritage, and exceptional quality. Incorporate longer, flowing sentences with elegant phrasing. Position products as investments rather than purchases. Ideal for upscale brands targeting discerning customers."
  },
  {
    value: "Bold & Edgy",
    description: "For streetwear, urban brands, or brands with attitude",
    example: "Slim enough to disappear in your pocket. Strong enough to outlast trends. This Italian leather wallet doesn't just hold your cash—it holds its own. With built-in RFID protection, it's got your back.",
    prompt: "Develop content with attitude and confidence. Use powerful, declarative statements and dynamic language. Incorporate unexpected comparisons or metaphors that challenge conventions. Balance assertiveness with authenticity, avoiding pretentiousness. Create a sense of rebellion or distinction from mainstream alternatives. Ideal for brands wanting to project strength and uniqueness."
  },
  {
    value: "Warm & Friendly",
    description: "For approachable, community-driven brands",
    example: "Meet your new favorite wallet—crafted from Italian leather, designed to keep your essentials safe (and stylish). It's got RFID protection, a timeless design, and just the right amount of space for what matters.",
    prompt: "Write in a conversational, approachable tone that feels personal and inclusive. Use welcoming language that builds connection, including first and second person pronouns. Balance warmth with informative content, ensuring product benefits are clearly communicated. Create a sense of community and shared values. Ideal for brands that want to feel accessible and relationship-focused."
  },
  {
    value: "Storytelling & Nostalgic",
    description: "For brands that love weaving a narrative",
    example: "Imagine: A cozy café in Milan, espresso in hand, this Italian leather wallet resting on the table. Designed with old-world craftsmanship and modern security, it's a piece of history you carry with you—RFID protection included.",
    prompt: "Craft narrative-driven content that places products within meaningful contexts or scenarios. Evoke emotion through sensory details and immersive descriptions. Connect products to experiences, memories, or aspirations. Balance storytelling with practical information about features and benefits. Use language that transports the customer to a specific time, place, or feeling. Ideal for brands that want to create emotional connections through their marketing."
  }
];

type GeneralSettingsSummaryProps = {
  visibleSections: Record<string, any>;
  languages: {
    value: string;
    label: string;
  }[];
  language: string;
  writingStyle: string;
  includedKeywords: string[];
  excludedKeywords: string[];
};

export const GeneralSettingsSummary = ({
  visibleSections,
  languages,
  language,
  writingStyle,
  includedKeywords,
  excludedKeywords,
}: GeneralSettingsSummaryProps) => {
  const { t } = useTranslation();

  return (
    <div>
      <CardTitle>{t('enrichmentConfig.general_settings')}</CardTitle>
      <CardDescription>
        {t('enrichmentConfig.basic_preferences')}
      </CardDescription>
      {!visibleSections.general && (
        <div className="mt-2 flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            <span>
              {languages.find((l) => l.value === language)?.label || language}
            </span>
          </div>
          <div className="w-1 h-1 bg-gray-300 rounded-full" />
          <div className="flex items-center gap-2">
            <Type className="w-4 h-4" />
            <span className="capitalize">{writingStyle} {t('enrichmentConfig.tone')}</span>
          </div>
          {(includedKeywords.length > 0 || excludedKeywords.length > 0) && (
            <>
              <div className="w-1 h-1 bg-gray-300 rounded-full" />
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4" />
                <span>
                  {includedKeywords.length + excludedKeywords.length} {t('enrichmentConfig.keywords_count')}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Component for tone example display
interface ToneExampleCardProps {
  tone: {
    value: string;
    description: string;
    example: string;
  };
  isPreview?: boolean;
}

const ToneExampleCard: React.FC<ToneExampleCardProps> = ({ tone, isPreview = false }) => {
  const { t } = useTranslation();
  
  if (!tone || !tone.value) {
    return null; // Safe fallback if tone data is missing
  }
  
  return (
    <div className="w-full rounded-lg overflow-hidden border border-gray-200 bg-gradient-to-r from-gray-50 to-white shadow-sm transition-all duration-300 ease-in-out">
      <div className="flex flex-col border-b border-gray-200 bg-gray-50 px-4 py-2">
        <div className="flex items-center gap-2">
          <Type className="h-4 w-4 text-gray-500" />
          <span className="font-medium text-gray-700">
            {isPreview ? t('enrichmentConfig.preview') : t('enrichmentConfig.example')}
            {tone.value}
          </span>
        </div>
        <span className="text-xs text-gray-500 italic mt-1">{tone.description}</span>
      </div>
      <div className="p-4 text-gray-700 bg-white">
        <p className="leading-relaxed">{tone.example}</p>
      </div>
    </div>
  );
};

type GeneralSettingsProps = {
  errors: {
    field: string;
    message: string;
  }[];
  language: string;
  setLanguage: (language: string) => void;
  writingStyle: string;
  setWritingStyle: (style: string) => void;
  writingStyleOptions: string[];
  includedKeywords: string[];
  setIncludedKeywords: (keywords: string[]) => void;
  handleKeyPress: (e: React.KeyboardEvent<HTMLInputElement>, type: "included" | "excluded") => void;
  addKeyword: (keyword: string, type: "included" | "excluded") => void;
  excludedKeywords: string[];
  setExcludedKeywords: (keywords: string[]) => void;
  languageOptions: {
    value: string;
    label: string;
}[];
};

export const GeneralSettings = ({ errors, language, setLanguage, writingStyle, setWritingStyle, writingStyleOptions, includedKeywords, setIncludedKeywords, handleKeyPress, addKeyword, excludedKeywords, setExcludedKeywords, languageOptions }: GeneralSettingsProps) => {
  const { t } = useTranslation();
  const [isLanguagePopoverOpen, setIsLanguagePopoverOpen] = useState(false);
  const [hoveredTone, setHoveredTone] = useState("");
  const [toneOfVoiceOptions, setToneOfVoiceOptions] = useState(toneOfVoiceOptionsList);
  // Get the currently selected tone example if available
  const selectedTone = toneOfVoiceOptions.find(tone => tone.value === writingStyle);

  return (
    <CardContent className="space-y-6">
      <SectionHeader
        icon={Globe}
        title={t('enrichmentConfig.language_style')}
        description={t('enrichmentConfig.set_tone_language')}
      />

      {/* Restructured Language & Tone settings */}
      <div className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          <PreferenceItem
            label={t('enrichmentConfig.language')}
            tooltip={t('enrichmentConfig.language')}
            fieldName="general.language"
            errors={errors}
          >
            <Popover open={isLanguagePopoverOpen} onOpenChange={setIsLanguagePopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between"
                >
                  {language
                    ? languageOptions.find((l) => l.value === language)?.label
                    : t('enrichmentConfig.select_language')}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder={t('enrichmentConfig.search_language')} />
                  <CommandEmpty>{t('enrichmentConfig.no_language_found')}</CommandEmpty>
                  <CommandList>
                    <CommandGroup>
                      {languageOptions.map((_language) => (
                        <CommandItem
                          key={_language.value}
                          value={_language.value}
                          onSelect={(currentValue) => {
                            setLanguage(currentValue);
                            setIsLanguagePopoverOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              language === _language.value ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {_language.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </PreferenceItem>

          <PreferenceItem
            label={t('enrichmentConfig.tone_of_voice')}
            tooltip={t('enrichmentConfig.tone_of_voice')}
            fieldName="general.writing_style"
            errors={errors}
          >
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className={cn(
                    "w-full justify-between",
                    writingStyle && "border-blue-200 bg-blue-50/50 text-blue-600"
                  )}
                >
                  {writingStyle ? (
                    <div className="flex items-center gap-2">
                      <Type className="h-4 w-4" />
                      <span>{writingStyle}</span>
                    </div>
                  ) : t('enrichmentConfig.select_tone')}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[750px] min-h-[600px]">
                <DialogHeader>
                  <DialogTitle>{t('enrichmentConfig.select_tone_of_voice')}</DialogTitle>
                </DialogHeader>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4 min-h-[400px]">
                  <div>
                    <div className="mb-3">
                      <input
                        type="text"
                        placeholder={t('enrichmentConfig.search_tone')}
                        className="w-full py-2 px-3 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-blue-300"
                        onChange={(e) => {
                          const searchQuery = e.target.value.toLowerCase();
                          setToneOfVoiceOptions(
                            toneOfVoiceOptionsList.filter(
                              (tone) =>
                                tone.value.toLowerCase().includes(searchQuery) ||
                                tone.description.toLowerCase().includes(searchQuery)
                            )
                          );
                        }}
                      />
                    </div>
                    <div className="max-h-[400px] overflow-y-auto border rounded-md">
                      {toneOfVoiceOptions.map((tone) => (
                        <div
                          key={tone.value}
                          className={cn(
                            "flex items-start p-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0",
                            tone.value === (hoveredTone || writingStyle) && "bg-blue-50"
                          )}
                          onClick={() => {
                            setWritingStyle(tone.value);
                            setHoveredTone("");
                            localStorage.setItem('writingStyle', tone.value);
                          }}
                          onMouseEnter={() => setHoveredTone(tone.value)}
                          onMouseLeave={() => setHoveredTone("")}
                        >
                          <div className="mr-2 mt-0.5">
                            {tone.value === writingStyle && (
                              <Check className="h-4 w-4 text-blue-500" />
                            )}
                          </div>
                          <div>
                            <div className="font-medium">{tone.value}</div>
                            <div className="text-xs text-gray-500">
                              {tone.description}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium mb-2">{t('enrichmentConfig.tone_preview')}</div>
                    {(hoveredTone || writingStyle) ? (
                      <div className="max-h-[430px] overflow-y-auto">
                        {(() => {
                          const toneToShow = toneOfVoiceOptions.find(
                            t => t.value === (hoveredTone || writingStyle)
                          );
                          return toneToShow ? (
                            <ToneExampleCard 
                              tone={toneToShow}
                              isPreview={!!hoveredTone}
                            />
                          ) : null;
                        })()}
                      </div>
                    ) : (
                      <div className="h-full flex items-center justify-center border rounded-md p-6 text-gray-400">
                        {t('enrichmentConfig.select_tone_to_see_example')}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <DialogTrigger asChild>
                    <Button onClick={() => setHoveredTone("")}>
                      {t('enrichmentConfig.apply_selection')}
                    </Button>
                  </DialogTrigger>
                </div>
              </DialogContent>
            </Dialog>
          </PreferenceItem>
            
          {/* Display example for selected tone outside dialog - made full width across all columns */}
          {selectedTone && (
            <div className="mt-4 col-span-1 md:col-span-2">
              <ToneExampleCard tone={selectedTone} />
            </div>
          )}
        </div>
      </div>

      <SectionHeader
        icon={Tag}
        title={t('enrichmentConfig.keywords')}
        description={t('enrichmentConfig.manage_keywords')}
      />

      <div className="space-y-3 pb-2">
        <PreferenceItem
          label={t('enrichmentConfig.include_keywords')}
          tooltip={t('enrichmentConfig.include_keywords_tooltip')}
          fieldName="general.include_keywords"
          errors={errors}
        >
          <div className="space-y-1.5">
            <div className="flex flex-wrap gap-1.5">
              {includedKeywords.map((keyword, index) => (
                <KeywordPill
                  key={index}
                  text={keyword}
                  onRemove={() => {
                    setIncludedKeywords(
                      includedKeywords.filter((_, i) => i !== index)
                    );
                  }}
                />
              ))}
            </div>
            <input
              type="text"
              placeholder={t('enrichmentConfig.add_keyword_enter')}
              className="w-full py-1.5 px-3 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-blue-300"
              onKeyUp={(e) => handleKeyPress(e, "included")}
              onBlur={(e) => {
                if (e.target.value) {
                  addKeyword(e.target.value, "included");
                  e.target.value = "";
                }
              }}
            />
            <div className="text-xs text-gray-500">
              {t('enrichmentConfig.suggested_include')}
            </div>
          </div>
        </PreferenceItem>

        <PreferenceItem
          label={t('enrichmentConfig.exclude_keywords')}
          tooltip={t('enrichmentConfig.exclude_keywords_tooltip')}
          fieldName="general.exclude_keywords"
          errors={errors}
        >
          <div className="space-y-1.5">
            <div className="flex flex-wrap gap-1.5">
              {excludedKeywords.map((keyword, index) => (
                <KeywordPill
                  key={index}
                  text={keyword}
                  onRemove={() => {
                    setExcludedKeywords(
                      excludedKeywords.filter((_, i) => i !== index)
                    );
                  }}
                />
              ))}
            </div>
            <input
              type="text"
              placeholder={t('enrichmentConfig.add_keyword_enter')}
              className="w-full py-1.5 px-3 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-blue-300"
              onKeyUp={(e) => handleKeyPress(e, "excluded")}
              onBlur={(e) => {
                if (e.target.value) {
                  addKeyword(e.target.value, "excluded");
                  e.target.value = "";
                }
              }}
            />
            <div className="text-xs text-gray-500">
              {t('enrichmentConfig.suggested_exclude')}
            </div>
          </div>
        </PreferenceItem>
      </div>
    </CardContent>
  );
};